import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../api";
import { Briefcase, Users, PlusCircle, Search, CalendarClock, BarChart3, GitBranch, ClipboardList, LayoutDashboard, History, Sparkles, ListChecks, BookOpen, FilePlus, Users2, Activity, Building2, ClipboardCheck, Pencil, UserPlus, FileSpreadsheet, X as IconX, Clock, FileText } from "lucide-react";
import Interviews from "./Interviews";
import Analytics from "./Analytics";
import CompanyPipeline from "./CompanyPipeline";
import CompanyOnboarding from "./CompanyOnboarding";
import CompanyOnboardingDetail from "./CompanyOnboardingDetail";
import NewPostingForm from "../components/company/NewPostingForm";
import AddCandidateModal from "../components/company/AddCandidateModal";
import BulkCsvImportModal from "../components/company/BulkCsvImportModal";
import CompanySourcedTalent from "./CompanySourcedTalent";
import DateTimePicker from "../components/ui/DateTimePicker";
import Avatar from "../components/ui/Avatar";
import RecruitmentDashboard from "./RecruitmentDashboard";
import CompanyRequisitions from "./CompanyRequisitions";
import CompanyApplicantHistory from "./CompanyApplicantHistory";
import { CompanyTasks, CompanyAssessments, CompanyReferrals, CompanyTalentPool, CompanyActivityLogs } from "./RecruitmentExtras";
import CompanyProfile from "./CompanyProfile";
import CompanyAssessmentsV2 from "./CompanyAssessmentsV2";
import usePageTitle from "../hooks/usePageTitle";
import { KpiTile, SectionCard, DashboardHeader } from "../components/dashboard/shells";

/**
 * StatusPill — friendly, colour-coded pill for application status.
 * Maps raw backend statuses (screening, interview_proposed, hired, ...) to
 * a human label + accent colour so recruiters can scan the applicant list.
 */
const STATUS_MAP = {
  applied:              { label: "Applied",              bg: "#EFF6FF", fg: "#1D4ED8" },
  screening:            { label: "In screening",         bg: "#F0F9FF", fg: "#0369A1" },
  shortlisted:          { label: "Shortlisted",          bg: "#F0FDF4", fg: "#15803D" },
  pending:              { label: "Pending review",       bg: "#F1F5F9", fg: "#475569" },
  interview_proposed:   { label: "Awaiting slot pick",   bg: "#FEF3C7", fg: "#92400E" },
  interview_scheduled:  { label: "Interview scheduled",  bg: "#FEF3C7", fg: "#B45309" },
  interview:            { label: "In interview",         bg: "#FEF3C7", fg: "#B45309" },
  offer:                { label: "Offer sent",           bg: "#F5F3FF", fg: "#6D28D9" },
  hired:                { label: "Hired",                bg: "#DCFCE7", fg: "#15803D" },
  onboarded:            { label: "Onboarded",            bg: "#DBEAFE", fg: "#1E40AF" },
  rejected:             { label: "Rejected",             bg: "#FEE2E2", fg: "#B91C1C" },
  withdrawn:            { label: "Withdrawn",            bg: "#F1F5F9", fg: "#64748B" },
};
/**
 * viewResume — fetches the candidate resume via the authenticated axios
 * client (localStorage bearer token is auto-attached) then opens the blob
 * in a new tab. Handles PDF / DOC / DOCX via native `content-type` header.
 * We can't just link `<a href="/api/...">` because our auth is bearer-based,
 * not cookie-based on all browsers (Safari ITP fix — see /app/SECURITY.md).
 */
async function viewResume(appId, onError) {
  try {
    const resp = await api.get(`/applications/${appId}/resume/download`, { responseType: "blob" });
    const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
    const url = URL.createObjectURL(blob);
    // Try open in new tab; fall back to force-download if popup blocked.
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      a.download = m ? m[1] : "resume.pdf";
      document.body.appendChild(a); a.click(); a.remove();
    }
    // Revoke after the tab has had a chance to load.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    // The response body is a Blob (responseType:"blob"), so the JSON error
    // detail isn't directly readable — decode it so the user sees the real
    // reason (e.g. "Candidate has not uploaded a resume yet") instead of a
    // cryptic "Request failed with status code 404".
    let msg = e?.message || "Failed to open resume";
    const data = e?.response?.data;
    if (data instanceof Blob) {
      try {
        const txt = await data.text();
        const j = JSON.parse(txt);
        if (j?.detail) msg = j.detail;
      } catch { /* non-JSON blob — keep the generic message */ }
    } else if (data?.detail) {
      msg = data.detail;
    }
    onError?.(msg);
  }
}

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: String(status || "—").replace(/_/g, " "), bg: "#F1F5F9", fg: "#475569" };
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}


const links = [
  { key: "home",        to: "/company/dashboard",           end: true, label: "Overview",          icon: Briefcase,        roles: ["*"] },
  { key: "recruitment", to: "/company/recruitment",         label: "Recruitment",                  icon: LayoutDashboard,  roles: ["*"] },
  { key: "reqs",        to: "/company/requisitions",        label: "Requisitions",                 icon: ClipboardList,    roles: ["requisition_creator", "hr_admin"] },
  { key: "search",      to: "/company/students",            label: "Talent Search",                icon: Users,            roles: ["recruiter", "hr_admin"] },
  { key: "post",        to: "/company/new",                 label: "Post a Role",                  icon: PlusCircle,       roles: ["hr_admin", "recruiter"] },
  { key: "mine",        to: "/company/postings",            label: "My Postings",                  icon: Briefcase,        roles: ["hr_admin", "recruiter", "hiring_manager"] },
  { key: "pipeline",    to: "/company/pipeline",            label: "Pipeline",                     icon: GitBranch,        roles: ["hiring_manager", "recruiter", "hr_admin"] },
  { key: "iv",          to: "/company/interviews",          label: "Interviews",                   icon: CalendarClock,    roles: ["hiring_manager", "panel_member", "recruiter", "hr_admin"] },
  { key: "history",     to: "/company/applicant-history",   label: "Applicant History",            icon: History,          roles: ["recruiter", "hr_admin", "hiring_manager"] },
  { key: "tasks",       to: "/company/tasks",               label: "Tasks",                        icon: ListChecks,       roles: ["recruiter"] },
  { key: "assess",      to: "/company/assessments",         label: "Question Banks",               icon: BookOpen,         roles: ["hr_admin"] },
  { key: "assess-v2",   to: "/company/assessments-v2",      label: "Test Engine",                  icon: ClipboardCheck,   roles: ["hr_admin"] },
  { key: "refs",        to: "/company/referrals",           label: "Referrals",                    icon: FilePlus,         roles: ["recruiter", "hr_admin"] },
  { key: "talent",      to: "/company/talent-pool",         label: "Talent Pool",                  icon: Users2,           roles: ["recruiter", "hr_admin"] },
  { key: "sourced",     to: "/company/sourced-candidates",  label: "Sourced Talent",               icon: Users2,           roles: ["recruiter", "hr_admin"] },
  { key: "activity",    to: "/company/activity-logs",       label: "Activity Logs",                icon: Activity,         roles: ["hr_admin"] },
  { key: "onboarding",  to: "/company/onboarding",          label: "Offers & Onboarding",          icon: ClipboardList,    roles: ["hr_admin"] },
  { key: "analytics",   to: "/company/analytics",           label: "Hiring Analytics",             icon: BarChart3,        roles: ["hr_admin", "hiring_manager"] },
  { key: "profile",     to: "/company/profile",             label: "Company Profile",              icon: Building2,        roles: ["*"] },
];

// Sub-role → human-readable label
const SUB_ROLE_LABELS = {
  "":                   "All access (owner)",
  requisition_creator:  "Requisition Creator",
  hr_admin:             "HR Administrator",
  recruiter:            "Recruiter",
  hiring_manager:       "Hiring Manager",
  panel_member:         "Interview Panel Member",
};

function scopedLinks(sub_role) {
  // No sub_role set → owner view, show everything
  if (!sub_role) return links;
  return links.filter((l) => l.roles.includes("*") || l.roles.includes(sub_role));
}

function SubRolePicker({ value, onChange }) {
  return (
    <div className="card card-pad flex items-center gap-3 flex-wrap" data-testid="sub-role-picker">
      <span className="eyebrow shrink-0">View as</span>
      <select
        className="input max-w-xs" value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid="sub-role-select"
      >
        {Object.entries(SUB_ROLE_LABELS).map(([v, lbl]) => (
          <option key={v || "owner"} value={v}>{lbl}</option>
        ))}
      </select>
      <span className="text-xs text-muted">Switch sidebar/access — applies only to this user.</span>
    </div>
  );
}

function Overview() {
  usePageTitle("Hiring Console");
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/postings/mine").then(r => r.data).catch(() => []),
      api.get("/company/interviews").then(r => r.data).catch(() => []),
    ]).then(([m, i]) => {
      if (!alive) return;
      setMine(m || []); setIvs(i || []); setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  const activePostings = mine.filter(p => p.status === "active").length;
  // Sum applications across all my postings — approximates "In pipeline"
  const totalApps = mine.reduce((s, p) => s + (p.applications_count || 0), 0);
  // Interview status vocabulary: {scheduled, proposed, completed, cancelled, no_show}
  const upcomingIvs = ivs.filter(i => {
    if (!["scheduled", "proposed"].includes(i.status)) return false;
    const start = i.slot_start || i.scheduled_at;
    return start && new Date(start) > new Date();
  }).length;
  const hired = mine.reduce((s, p) => s + (p.hired_count || 0), 0);
  return (
    <div className="space-y-4">
      <DashboardHeader
        eyebrow="Hiring Console"
        title="Hire talent, fast."
        subtitle="Post a role, review applications, interview panels and offer letters — all in one place."
        primary={{ to: "/company/new",       label: "+ New posting",     testid: "co-cta-post" }}
        secondary={{ to: "/company/students", label: "Search students",  testid: "co-cta-search" }}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          icon={Briefcase}
          label="Active postings"
          value={activePostings}
          sub={mine.length ? `${mine.length} total` : "No postings yet"}
          accent="#006CFF"
          to="/company/postings"
          testid="co-postings-count"
          loading={loading}
          cta="Manage"
        />
        <KpiTile
          icon={Users}
          label="In pipeline"
          value={totalApps}
          sub={mine.length ? `Across ${mine.length} posting${mine.length === 1 ? "" : "s"}` : "Post a role to start"}
          accent="#9933FF"
          to="/company/pipeline"
          testid="kpi-pipeline"
          loading={loading}
          cta="Open"
        />
        <KpiTile
          icon={CalendarClock}
          label="Upcoming interviews"
          value={upcomingIvs}
          sub={ivs.length ? `${ivs.length} scheduled overall` : "None scheduled"}
          accent="#F59E0B"
          to="/company/interviews"
          testid="kpi-ivs"
          loading={loading}
          cta="Schedule"
        />
        <KpiTile
          icon={ClipboardCheck}
          label="Hired"
          value={hired}
          sub="Offers accepted"
          accent="#10B981"
          to="/company/onboarding"
          testid="kpi-hired"
          loading={loading}
          cta="Onboarding"
        />
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-4">
        <SectionCard
          eyebrow="Recent postings"
          title="Your active roles"
          action={<Link to="/company/postings" className="text-xs link-underline text-signal font-semibold">Manage all →</Link>}
          testid="co-recent-postings"
        >
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 bg-slate-50 rounded-md animate-pulse" />
              ))}
            </div>
          ) : mine.length === 0 ? (
            <div className="text-sm text-slate-600 py-4 text-center">
              No postings yet. <Link to="/company/new" className="text-signal font-semibold hover:underline">Post your first role</Link>.
            </div>
          ) : (
            <div className="space-y-2">
              {mine.slice(0, 4).map(p => (
                <Link
                  key={p.id}
                  to={`/company/postings`}
                  className="block p-3 border border-rule rounded-md hover:border-signal/40 hover:bg-slate-50/60 transition"
                  data-testid={`co-recent-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{p.title}</div>
                      <div className="text-xs text-muted mt-0.5">
                        {p.type} · {p.location || "Remote"} · {p.applications_count || 0} applicant{p.applications_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          eyebrow="Coming up"
          title="Next interviews"
          action={<Link to="/company/interviews" className="text-xs link-underline text-signal font-semibold">All →</Link>}
          testid="co-upcoming-ivs"
        >
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-14 bg-slate-50 rounded-md animate-pulse" />
              ))}
            </div>
          ) : upcomingIvs === 0 ? (
            <p className="text-sm text-slate-600">No interviews scheduled.</p>
          ) : (
            <ul className="space-y-2">
              {ivs
                .filter(i => {
                  if (!["scheduled", "proposed"].includes(i.status)) return false;
                  const start = i.slot_start || i.scheduled_at;
                  return start && new Date(start) > new Date();
                })
                .slice(0, 4)
                .map(iv => {
                  const start = iv.slot_start || iv.scheduled_at;
                  return (
                    <li
                      key={iv.id}
                      className="p-2.5 border border-rule rounded-md hover:border-signal/40 transition"
                      data-testid={`co-iv-${iv.id}`}
                    >
                      <div className="font-semibold text-sm truncate">{iv.candidate_name || iv.student_name || "Candidate"}</div>
                      <div className="text-xs text-muted mt-0.5 tabular-nums">
                        {new Date(start).toLocaleString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function NewPosting() {
  return <NewPostingForm />;
}

function MyPostings() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [apps, setApps] = useState([]);
  const [recs, setRecs] = useState([]);
  const [tab, setTab] = useState("apps"); // apps | recs
  const [schedFor, setSchedFor] = useState(null); // application object
  const [editFor, setEditFor] = useState(null);   // posting object being edited
  const [addCandFor, setAddCandFor] = useState(null); // posting object for manual add
  const [bulkCsvFor, setBulkCsvFor] = useState(null); // posting object for CSV import
  const load = () => api.get("/postings/mine").then(r=>setList(r.data));
  useEffect(()=>{ load(); }, []);
  const viewApps = async (p) => {
    setSelected(p); setTab("apps"); setSchedFor(null);
    const [r1, r2] = await Promise.all([
      api.get(`/postings/${p.id}/applications`),
      api.get(`/postings/${p.id}/recommendations`),
    ]);
    setApps(r1.data); setRecs(r2.data);
  };
  const del = async (id) => { if (!window.confirm("Delete this posting?")) return; await api.delete(`/postings/${id}`); load(); };
  const onEditSaved = (updated) => {
    setEditFor(null); load();
    if (selected && updated && selected.id === updated.id) {
      setSelected(updated);
    }
  };
  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-2 flex flex-col gap-3">
        {list.map(p => (
          <div key={p.id} className="card card-pad" data-testid={`mine-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-display font-bold text-lg">{p.title}</h4>
                <div className="text-sm text-muted">{p.type} · {p.location || "—"}</div>
                {p.sc_domain === "OTHER" ? (
                  <span className="inline-block text-[10px] font-mono mt-1 px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">OTHER · Non-Technical</span>
                ) : p.sc_domain ? (
                  <span className="inline-block text-[10px] font-mono mt-1 px-1.5 py-0.5 rounded-sm bg-signal/10 text-signal border border-signal/30">{p.sc_domain}</span>
                ) : null}
              </div>
              <span className="badge badge-signal">{p.status}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="btn-ghost" onClick={()=>viewApps(p)} data-testid={`view-apps-${p.id}`}>View applicants</button>
              <button className="btn-ghost" onClick={()=>setAddCandFor(p)} data-testid={`add-candidate-btn-${p.id}`}><UserPlus size={12}/> Add candidate</button>
              <button className="btn-ghost" onClick={()=>setBulkCsvFor(p)} data-testid={`bulk-csv-btn-${p.id}`}><FileSpreadsheet size={12}/> Import CSV</button>
              <button className="btn-ghost" onClick={()=>setEditFor(p)} data-testid={`edit-posting-${p.id}`}><Pencil size={12}/> Edit</button>
              <button className="btn-ghost" onClick={()=>del(p.id)} data-testid={`delete-posting-${p.id}`}>Delete</button>
            </div>
          </div>
        ))}
        {list.length===0 && <div className="card card-pad text-muted">No postings yet.</div>}
      </div>
      <div className="md:col-span-3 card card-pad">
        {!selected && <p className="text-muted">Pick a posting to see applicants and AI-matched recommendations.</p>}
        {selected && (<>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="eyebrow">Applicants & matches</span>
              <div className="font-display font-bold text-xl mt-0.5">{selected.title}</div>
            </div>
            <div className="flex border border-rule rounded-sm">
              {[["apps", `Applied (${apps.length})`], ["recs", `Suggested (${recs.length})`]].map(([k, lbl]) => (
                <button key={k} onClick={()=>setTab(k)} data-testid={`co-tab-${k}`}
                  className={`px-3 py-1.5 text-sm font-semibold ${tab===k ? "bg-signal text-white" : "hover:bg-paper"}`}>{lbl}</button>
              ))}
            </div>
          </div>

          {tab === "apps" && (
            <table className="dense mt-4"><thead><tr><th>Name</th><th>CoE</th><th>Resume</th><th>Status</th><th></th></tr></thead>
            <tbody>{apps.map(a=><tr key={a.id}>
              <td>
                <div className="flex items-center gap-2.5">
                  <Avatar src={a.student_photo_url} name={a.student_name} size={32} testid={`app-avatar-${a.id}`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.student_name}</div>
                    <div className="text-xs text-muted truncate">{a.student_email}</div>
                  </div>
                </div>
              </td>
              <td><div className="flex flex-wrap gap-1">{(a.coe_badges||[]).map(b => (
                <span key={b.lab_id} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm"
                  style={{ background: `${b.lab_color}15`, color: b.lab_color, border: `1px solid ${b.lab_color}40` }}
                  data-testid={`app-badge-${a.id}-${b.lab_code}`}>{b.lab_code}</span>
              ))}</div></td>
              <td>
                {a.has_resume ? (
                  <button
                    type="button"
                    onClick={() => viewResume(a.id, (msg) => alert(msg))}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-signal hover:underline"
                    data-testid={`view-resume-${a.id}`}
                    title={a.resume_filename || "Open resume in new tab"}
                  >
                    <FileText size={12} /> View
                  </button>
                ) : (
                  <span className="text-[10px] text-muted italic" data-testid={`no-resume-${a.id}`}>no resume</span>
                )}
              </td>
              <td><StatusPill status={a.status} />
                {a.is_sourced && (
                  <span className="ml-1 inline-block text-[9px] font-mono px-1 py-0.5 rounded-sm bg-purple-50 text-purple-700 border border-purple-200 uppercase" data-testid={`sourced-pill-${a.id}`}>
                    via {a.source || "manual"}
                  </span>
                )}
              </td>
              <td>
                {["interview_proposed", "interview_scheduled"].includes(a.status) ? (
                  <button
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border bg-amber-50 text-amber-800 border-amber-200"
                    onClick={()=>setSchedFor(a)}
                    data-testid={`view-interview-${a.id}`}
                    title="An interview is already active — click to view / reschedule"
                  >
                    <Clock size={12}/> {a.status === "interview_proposed" ? "Awaiting slot" : "Scheduled"}
                  </button>
                ) : (
                  <button className="btn-ghost" onClick={()=>setSchedFor(a)} data-testid={`schedule-${a.id}`}>
                    <CalendarClock size={12}/> Schedule
                  </button>
                )}
              </td></tr>)}
            {apps.length===0 && <tr><td colSpan={5} className="text-muted">No applicants yet.</td></tr>}</tbody></table>
          )}

          {tab === "recs" && (
            <div className="mt-4 space-y-2">
              {recs.length === 0 ? <div className="text-muted text-sm">No matching students found.</div> :
                recs.map(s => (
                  <div key={s.user_id} className="flex items-start justify-between gap-3 p-3 border border-rule rounded-sm" data-testid={`rec-${s.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted">{s.institute_name || ""} · {s.course || ""}</div>
                      <div className="text-xs text-muted mt-1">{(s.match_reasons || []).join(" · ")}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(s.coe_badges || []).slice(0, 3).map(b => (
                          <span key={b.lab_id} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm"
                            style={{ background: `${b.lab_color}15`, color: b.lab_color, border: `1px solid ${b.lab_color}40` }}>
                            {b.lab_code}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-signal"><Sparkles size={12}/> {s.match_score}%</span>
                      <span className="text-[10px] text-muted">Match</span>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {schedFor && <ScheduleModal app={schedFor} onClose={()=>setSchedFor(null)} onDone={()=>{ setSchedFor(null); viewApps(selected); }} />}
        </>)}
      </div>
      {editFor && <EditPostingModal posting={editFor} onClose={()=>setEditFor(null)} onSaved={onEditSaved} />}
      {addCandFor && (
        <AddCandidateModal
          posting={addCandFor}
          onClose={()=>setAddCandFor(null)}
          onAdded={()=>{
            const p = addCandFor;
            setAddCandFor(null);
            // Open the applicants panel for this posting so the recruiter
            // sees the new row immediately and can hit Schedule.
            viewApps(p);
          }}
        />
      )}
      {bulkCsvFor && (
        <BulkCsvImportModal
          posting={bulkCsvFor}
          onClose={()=>setBulkCsvFor(null)}
          onDone={()=>{
            const p = bulkCsvFor;
            setBulkCsvFor(null);
            viewApps(p);
          }}
        />
      )}
    </div>
  );
}

function EditPostingModal({ posting, onClose, onSaved }) {
  const onBackdrop = (e) => {
    // Only close on direct backdrop tap, not on bubbling clicks from the
    // dialog contents (avoids accidental dismiss on mobile while editing).
    if (e.target === e.currentTarget) onClose();
  };
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onBackdrop}
      data-testid="edit-posting-modal"
    >
      <div
        className="bg-white rounded-sm max-w-3xl w-full my-8"
        onClick={e=>e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-rule">
          <div>
            <span className="eyebrow">Edit posting</span>
            <h3 className="font-display font-bold text-xl mt-0.5">{posting.title}</h3>
          </div>
          <button
            className="btn-ghost"
            onClick={onClose}
            data-testid="edit-posting-close"
            aria-label="Close edit modal"
          >
            <IconX size={16}/>
          </button>
        </div>
        <div className="p-5">
          <NewPostingForm editing={posting} onSaved={onSaved} />
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({ app, onClose, onDone }) {
  const [slots, setSlots] = useState(["", "", ""]);
  const [mode, setMode] = useState("video");
  const [link, setLink] = useState("");
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async () => {
    setMsg("");
    const cleaned = slots.filter(Boolean).map(s => new Date(s).toISOString());
    if (cleaned.length < 1) { setMsg("Add at least one slot."); return; }
    try {
      await api.post(`/applications/${app.id}/interview`, { slots: cleaned, mode, meeting_link: link, notes });
      onDone();
    } catch (e) { setMsg(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm max-w-lg w-full card-pad" onClick={e=>e.stopPropagation()} data-testid="schedule-modal">
        <span className="eyebrow">Propose interview</span>
        <h3 className="font-display font-bold text-xl mt-1">Schedule with {app.student_name}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="eyebrow">Proposed slots (up to 5)</label>
            {slots.map((s, i) => (
              <div key={`slot-${i}`} className="mt-2" data-testid={`slot-${i}`}>
                <DateTimePicker
                  value={s}
                  onChange={(v) => setSlots(slots.map((sv, j) => j === i ? v : sv))}
                  testIdPrefix={`slot-${i}-dtp`}
                  placeholder={`Slot ${i + 1} — pick date & time`}
                />
              </div>
            ))}
            {slots.length < 5 && <button className="btn-ghost mt-2" onClick={()=>setSlots([...slots, ""])} data-testid="schedule-add-slot">+ Add slot</button>}
          </div>
          <div><label className="eyebrow">Mode</label>
            <select className="input mt-2" value={mode} onChange={e=>setMode(e.target.value)} data-testid="iv-mode">
              <option value="video">Video call</option>
              <option value="onsite">On-site</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <div><label className="eyebrow">Meeting link (optional)</label>
            <input className="input mt-2" placeholder="https://meet.google.com/..." value={link} onChange={e=>setLink(e.target.value)} data-testid="iv-link" />
          </div>
          <div><label className="eyebrow">Notes (optional)</label>
            <textarea className="input mt-2" value={notes} onChange={e=>setNotes(e.target.value)} data-testid="iv-notes" />
          </div>
        </div>
        {msg && <div className="text-red-600 text-sm mt-2" data-testid="schedule-msg">{msg}</div>}
        <div className="mt-5 flex gap-3 justify-end">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} data-testid="schedule-send">Send proposal</button>
        </div>
      </div>
    </div>
  );
}

function SearchStudents() {
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("");
  const [list, setList] = useState([]);
  const load = useCallback(
    () => api.get("/students", { params: { q: q || undefined, skill: skill || undefined } }).then(r=>setList(r.data)),
    [q, skill]
  );
  useEffect(()=>{ load(); /* initial load only — manual search via button */ }, []);
  return (
    <>
      <div className="card card-pad flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]"><label className="eyebrow">Search (name / institute / course)</label><input className="input mt-2" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="stu-search-q" /></div>
        <div><label className="eyebrow">Skill</label><input className="input mt-2" value={skill} onChange={(e)=>setSkill(e.target.value)} data-testid="stu-search-skill" /></div>
        <button className="btn-primary" onClick={load} data-testid="stu-search-btn">Search</button>
      </div>
      <div className="card mt-6 overflow-x-auto">
        <table className="dense"><thead><tr><th>Name</th><th>Institute</th><th>Course</th><th>Graduation</th><th>Skills</th><th>CoE Badges</th></tr></thead>
        <tbody>{list.map(s => (<tr key={s.user_id} data-testid={`stu-row-${s.user_id}`}><td>{s.name}</td><td>{s.institute_name || "—"}</td><td>{s.course || "—"}</td><td>{s.graduation_year || "—"}</td><td>{(s.skills||[]).slice(0,4).join(", ")}</td>
        <td><div className="flex flex-wrap gap-1">{(s.coe_badges||[]).map(b => (
          <span key={b.lab_id} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm" title={`Trained at ${b.lab_name}`}
            style={{ background: `${b.lab_color}15`, color: b.lab_color, border: `1px solid ${b.lab_color}40` }}
            data-testid={`badge-${s.user_id}-${b.lab_code}`}>
            {b.lab_code}
          </span>
        ))}</div></td></tr>))}
        {list.length===0 && <tr><td colSpan={6} className="text-muted">No students match.</td></tr>}</tbody></table>
      </div>
    </>
  );
}

export default function CompanyDashboard() {
  const { user, refresh } = useAuth();
  const [subRole, setSubRole] = useState(user?.sub_role || "");

  // Keep local state in sync if the auth user object changes (e.g. login refresh).
  useEffect(() => { setSubRole(user?.sub_role || ""); }, [user?.sub_role]);

  const onChangeSubRole = async (newRole) => {
    setSubRole(newRole || "");
    try {
      await api.patch("/me/sub-role", { sub_role: newRole || null });
      refresh?.();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("sub_role update failed:", e?.message || e);
    }
  };

  const visibleLinks = scopedLinks(subRole);

  return (
    <Layout title="Hiring Console" links={visibleLinks}>
      <Routes>
        <Route index element={<><SubRolePicker value={subRole} onChange={onChangeSubRole} /><div className="mt-6"><Overview /></div></>} />
        <Route path="dashboard" element={<><SubRolePicker value={subRole} onChange={onChangeSubRole} /><div className="mt-6"><Overview /></div></>} />
        <Route path="recruitment" element={<RecruitmentDashboard user={user} />} />
        <Route path="requisitions" element={<CompanyRequisitions />} />
        <Route path="applicant-history" element={<CompanyApplicantHistory />} />
        <Route path="tasks" element={<CompanyTasks />} />
        <Route path="assessments" element={<CompanyAssessments />} />
        <Route path="referrals" element={<CompanyReferrals />} />
        <Route path="talent-pool" element={<CompanyTalentPool />} />
        <Route path="sourced-candidates" element={<CompanySourcedTalent />} />
        <Route path="activity-logs" element={<CompanyActivityLogs />} />
        <Route path="new" element={<NewPosting />} />
        <Route path="postings" element={<MyPostings />} />
        <Route path="students" element={<SearchStudents />} />
        <Route path="interviews" element={<Interviews />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="pipeline" element={<CompanyPipeline />} />
        <Route path="profile" element={<CompanyProfile />} />
        <Route path="assessments-v2" element={<CompanyAssessmentsV2 />} />
        <Route path="onboarding" element={<CompanyOnboarding />} />
        <Route path="onboarding/:id" element={<CompanyOnboardingDetail />} />
        <Route path="*" element={<Overview />} />
      </Routes>
    </Layout>
  );
}
